import React, { createContext, useState, useEffect, useContext, useMemo } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  OAuthProvider,    
  signInWithRedirect,
  signInWithPopup,
  signInWithCredential,
  getRedirectResult,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";


import { auth } from "../firebase"; 

//globale Variable/Information in deinem gesamten React-Programm verfügbar machen, 
//ohne es durch jede einzelne Komponente weiterzureichen
export const AuthContext = createContext();

//useState ist ein React Hook, der es dir ermöglicht, Zustand (State) in funktionalen Komponenten zu verwalten.
//Variablen definieren, deren Werte sich im Laufe der Zeit ändern können

//useEffect ist ein weiterer React Hook, der es dir ermöglicht, Nebenwirkungen (Side Effects) 
// in einer funktionalen Komponente auszuführen (Daten abfragen, API-Aufrufe, Abmelden von Events)

//useContext ist ein React Hook, der es dir ermöglicht, auf den Wert eines Kontexts zuzugreifen, der mit createContext erstellt wurde.
//- ohne die Daten durch viele Komponenten weiterreichen zu müssen.
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // raw Firebase user (web)
  const [loading, setLoading] = useState(true);
  console.log("[Auth] AuthProvider render", { loading, uid: user?.uid ?? null });

  // 1) Auth state subscription (unchanged behavior)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Optional debug
  useEffect(() => {
    console.log("Auth state changed:", user?.uid || null);
  }, [user]);  

  // 2) Actions (centralized here)

  // Email/password sign-in
  const signInEmailPassword = async (email, password) => {
    const res = await signInWithEmailAndPassword(auth, email, password);
    return res.user;
  };

  // Email/password sign-up (does NOT write Firestore user doc — you keep that in SignupPage for now)
  const signUpEmailPassword = async (email, password) => {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    return res.user;
  };

  // Send verification to current user (or an explicitly passed user)
  const sendVerificationEmail = async (targetUser = auth.currentUser) => {
    if (!targetUser) throw new Error("No authenticated user to verify.");
    await sendEmailVerification(targetUser);
  };

  // Reload current user (useful after verifying email)
  const reloadUser = async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    // onAuthStateChanged may not fire on reload, so also refresh local state
    setUser(auth.currentUser);
  };

  // Password reset
  const sendPasswordReset = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  // Sign out
  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  // Reauthenticate current user with password (used for delete-account)
  const reauthenticateWithPassword = async (password) => {
    const current = auth.currentUser;
    if (!current?.email) throw new Error("No authenticated email user.");
    const credential = EmailAuthProvider.credential(current.email, password);
    await reauthenticateWithCredential(current, credential);
  };

  // Delete current user (auth account) — does NOT delete Firestore data (you do that separately for now)
  const deleteCurrentUser = async () => {
    const current = auth.currentUser;
    if (!current) throw new Error("No authenticated user.");
    await current.delete();
  };

  // Google redirect sign-in web and native
  const signInWithGooglePopup = async () => {
    // WEB: keep current behavior
    if (!Capacitor.isNativePlatform()) {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: "select_account" });
      const res = await signInWithPopup(auth, provider);
      return res.user;
    }
  
    // NATIVE: use plugin -> then bridge into Firebase JS SDK session
    const result = await FirebaseAuthentication.signInWithGoogle();
    const idToken = result?.credential?.idToken;
  
    if (!idToken) throw new Error("Native Google sign-in did not return an idToken.");
  
    const credential = GoogleAuthProvider.credential(idToken);
    const res = await signInWithCredential(auth, credential);
    return res.user;
  };

    // Apple sign-in (web: not supported / native: plugin -> bridge into Firebase JS session)
    const signInWithApple = async () => {
      // On web we just block or hide the button.
      if (!Capacitor.isNativePlatform()) {
        throw new Error("Sign in with Apple is only available in the iOS app.");
      }
  
      // Native: Apple system sheet via plugin
      const result = await FirebaseAuthentication.signInWithApple();
  
      const idToken = result?.credential?.idToken;
      const rawNonce = result?.credential?.nonce;
  
      if (!idToken) {
        throw new Error("Apple sign-in did not return an idToken.");
      }
      if (!rawNonce) {
        throw new Error("Apple sign-in did not return a nonce.");
      }
  
      // Bridge into Firebase Web SDK session
      const provider = new OAuthProvider("apple.com");
      const credential = provider.credential({
        idToken,
        rawNonce, // ✅ critical for Apple + Firebase
      });
  
      const res = await signInWithCredential(auth, credential);
      return res.user;
    };
  


// This was used for SigninwithGoogleRedirect, not neccessary anymore since popup is used now.
// const finalizeRedirectSignIn = async () => {
//   try {
//     console.log("[Auth] finalizeRedirectSignIn(): start");
//     console.log("[Auth] finalizeRedirectSignIn(): location", {
//       origin: window.location.origin,
//       href: window.location.href,
//     });

//     const result = await getRedirectResult(auth);

//     console.log("[Auth] finalizeRedirectSignIn(): raw result =", result);

//     if (!result) {
//       console.log("[Auth] finalizeRedirectSignIn(): result is null (no pending redirect)");
//       return null;
//     }

//     console.log("[Auth] finalizeRedirectSignIn(): providerId =", result?.providerId);
//     console.log("[Auth] finalizeRedirectSignIn(): user =", {
//       uid: result.user?.uid,
//       email: result.user?.email,
//       emailVerified: result.user?.emailVerified,
//       providers: result.user?.providerData?.map((p) => p.providerId),
//     });

//     return result.user ?? null;
//   } catch (err) {
//     console.error("[Auth] finalizeRedirectSignIn(): ERROR", {
//       code: err?.code,
//       message: err?.message,
//       name: err?.name,
//       stack: err?.stack,
//     });
//     return null;
//   }
// };


//   useEffect(() => {
//     // Only meaningful on web; harmless elsewhere
//     finalizeRedirectSignIn();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

  const value = useMemo(
    () => ({
      // state
      user,
      loading,
      isAuthenticated: !!user,

      // actions
      signInEmailPassword,
      signUpEmailPassword,
      sendVerificationEmail,
      reloadUser,
      sendPasswordReset,
      signOut,
      reauthenticateWithPassword,
      deleteCurrentUser,
      signInWithGooglePopup,
      signInWithApple,
    }),
    [user, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

